// Platform admin API client. Surfaces the small slice of
// /api/v1/platform/* endpoints the PlatformDashboard.tsx home page
// consumes:
//
//   - GET /api/v1/platform/members/stats     → platform member counts
//   - GET /api/v1/platform/v2-adoption       → v2 adoption stat strip
//   - GET /api/v1/organizations (perPage=1)  → org total count for the
//     "all tenants" KPI (re-uses the listOrgs envelope's pagination.total)
//
// CHG-1540 / ITER-3658 — IA correction landing slice. Hand-rolled per
// feedback_deterministic_codegen_over_hand_rolling.md (no platform-side
// generator yet; precedent: me-recurring.ts / me-overview.ts).

import { apiClient } from "./client";

export interface PlatformMemberStats {
  total?: number;
  // BE returns a free-form dict; surface the common keys we render
  // today + tolerate extras. Names mirror UserService.platform_stats().
  active?: number;
  inactive?: number;
  invited?: number;
  by_role?: Record<string, number>;
}

export interface V2AdoptionStat {
  // Each stat-strip cell from the v2-adoption endpoint. Names mirror
  // src/api/app/routers/platform_v2_adoption.py — we only need the
  // top-level counts for the dashboard KPI cards; the full time-series
  // + funnel sections are owned by V2AdoptionDashboard (CHG-1307).
  v1_weekly_active?: number;
  v2_weekly_active?: number;
  v1_total?: number;
  v2_total?: number;
}

export interface PlatformOverview {
  org_count: number;
  members: PlatformMemberStats;
  adoption: V2AdoptionStat;
}

interface OrgsPagedEnvelopeMin {
  data: unknown[];
  pagination?: { total?: number };
}

interface V2AdoptionResponse {
  stat_strip?: {
    v1_weekly_active?: number;
    v2_weekly_active?: number;
    v1_total?: number;
    v2_total?: number;
  };
}

async function fetchOrgCount(): Promise<number> {
  // Reuse the GET /organizations endpoint (already gated to
  // org-admin OR platform-admin). per_page=1 keeps the payload tiny;
  // pagination.total carries the canonical count. Falls back to data
  // array length when the envelope omits pagination (defensive — older
  // org-admin responses can lack the page metadata).
  const r = await apiClient().get<OrgsPagedEnvelopeMin>("/organizations", {
    params: { page: 1, per_page: 1 },
  });
  const total = r.data?.pagination?.total;
  if (typeof total === "number") return total;
  return Array.isArray(r.data?.data) ? r.data.data.length : 0;
}

async function fetchMemberStats(): Promise<PlatformMemberStats> {
  const r = await apiClient().get<PlatformMemberStats>(
    "/platform/members/stats",
  );
  return r.data ?? {};
}

async function fetchV2Adoption(): Promise<V2AdoptionStat> {
  const r = await apiClient().get<V2AdoptionResponse>("/platform/v2-adoption");
  const s = r.data?.stat_strip ?? {};
  return {
    v1_weekly_active: s.v1_weekly_active,
    v2_weekly_active: s.v2_weekly_active,
    v1_total: s.v1_total,
    v2_total: s.v2_total,
  };
}

export async function fetchPlatformOverview(): Promise<PlatformOverview> {
  // Three parallel calls — each is independently rate-limited and
  // independently authorized by the BE. Promise.all so a slow endpoint
  // doesn't stack-charge the page; a single rejected call surfaces via
  // react-query's isError and triggers the DashboardPage retry card.
  const [org_count, members, adoption] = await Promise.all([
    fetchOrgCount(),
    fetchMemberStats(),
    fetchV2Adoption(),
  ]);
  return { org_count, members, adoption };
}

export const platformApi = {
  fetchPlatformOverview,
};
